import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaShieldAlt, FaLock, FaDatabase, FaUserShield } from 'react-icons/fa';

const PrivacyPolicy = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle scroll-to-top button visibility
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return (
    <div className="legal-page">
      {/* Header */}
      <div className="legal-header">
        <div className="container">
          <Link to="/" className="back-link">
            <FaArrowLeft />
            <span>Back to Home</span>
          </Link>
          
          <div className="legal-title-section">
            <div className="legal-icon">
              <FaShieldAlt />
            </div>
            <h1 className="legal-title">Privacy Policy</h1>
            <p className="legal-subtitle">
              How Passly protects and handles your information
            </p>
            <div className="legal-meta">
              <span>Last updated: {new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="legal-content">
        <div className="container">
          <div className="legal-grid">
            
            {/* Table of Contents */}
            <div className="legal-toc">
              <h3>Table of Contents</h3>
              <ul>
                <li><a href="#overview">Overview</a></li>
                <li><a href="#information-we-collect">Information We Collect</a></li>
                <li><a href="#how-we-use">How We Use Information</a></li>
                <li><a href="#blockchain-data">Blockchain Data</a></li>
                <li><a href="#data-protection">Data Protection</a></li>
                <li><a href="#third-party">Third-Party Services</a></li>
                <li><a href="#your-rights">Your Rights</a></li>
                <li><a href="#data-retention">Data Retention</a></li>
                <li><a href="#contact">Contact Us</a></li>
              </ul>
            </div>

            {/* Main Content */}
            <div className="legal-main">
              
              <section id="overview" className="legal-section">
                <div className="section-header">
                  <FaLock className="section-icon" />
                  <h2>Privacy by Design</h2>
                </div>
                <div className="privacy-highlight">
                  <p>
                    <strong>Passly is built on privacy-first principles.</strong> Unlike traditional identity services, 
                    we use cryptographic proofs to verify your social accounts without storing your personal information. 
                    This policy explains how we protect your privacy while enabling decentralized identity verification.
                  </p>
                </div>
              </section>

              <section id="information-we-collect" className="legal-section">
                <h2>Information We Collect</h2>
                
                <div className="info-grid">
                  <div className="info-card">
                    <h4>🔗 What We Store</h4>
                    <ul>
                      <li>Your wallet address (already public on blockchain)</li>
                      <li>Platform verification status (e.g., "Twitter verified")</li>
                      <li>Cryptographic proof hashes</li>
                      <li>Verification timestamps</li>
                      <li>Passport category selection</li>
                    </ul>
                  </div>
                  
                  <div className="info-card">
                    <h4>❌ What We DON'T Store</h4>
                    <ul>
                      <li>Your social media usernames</li>
                      <li>Email addresses</li>
                      <li>Profile pictures or personal photos</li>
                      <li>Social media posts or content</li>
                      <li>Private keys or wallet seed phrases</li>
                    </ul>
                  </div>
                </div>

                <h3>Temporary Processing Data</h3>
                <p>
                  During verification, we temporarily process your social media profile information to generate 
                  cryptographic proofs. This data is immediately discarded after proof generation and is never 
                  stored in our systems.
                </p>

                <h3>Technical Data</h3>
                <p>
                  We collect standard web analytics data including IP addresses, browser types, and usage patterns 
                  to improve our service. This data is anonymized and used only for operational purposes.
                </p>
              </section>

              <section id="how-we-use" className="legal-section">
                <div className="section-header">
                  <FaDatabase className="section-icon" />
                  <h2>How We Use Information</h2>
                </div>
                
                <div className="use-cases">
                  <div className="use-case">
                    <h4>✅ Service Operation</h4>
                    <p>To provide identity verification services and maintain your passport</p>
                  </div>
                  
                  <div className="use-case">
                    <h4>🔧 Technical Improvement</h4>
                    <p>To improve our platform performance and user experience</p>
                  </div>
                  
                  <div className="use-case">
                    <h4>🛡️ Security & Fraud Prevention</h4>
                    <p>To detect and prevent abuse of our verification systems</p>
                  </div>
                  
                  <div className="use-case">
                    <h4>📞 Support</h4>
                    <p>To provide customer support and resolve technical issues</p>
                  </div>
                </div>

                <div className="privacy-notice">
                  <strong>We never sell, rent, or share your personal information with third parties for marketing purposes.</strong>
                </div>
              </section>

              <section id="blockchain-data" className="legal-section">
                <h2>Blockchain and Decentralized Data</h2>
                
                <div className="blockchain-info">
                  <h3>🔗 On-Chain Data</h3>
                  <p>
                    Your passport and verification proofs are stored on the blockchain, making them:
                  </p>
                  <ul>
                    <li><strong>Publicly viewable:</strong> Anyone can see that a wallet has verified certain platforms</li>
                    <li><strong>Permanent:</strong> Cannot be deleted or modified once recorded</li>
                    <li><strong>Decentralized:</strong> Not controlled by Passly or any single entity</li>
                  </ul>
                  
                  <h3>🔐 Privacy Protection</h3>
                  <p>
                    While verification status is public, your personal information remains private through:
                  </p>
                  <ul>
                    <li>Cryptographic hashing of your actual usernames</li>
                    <li>Zero-knowledge proof techniques</li>
                    <li>No storage of reversible personal identifiers</li>
                  </ul>
                </div>
              </section>

              <section id="data-protection" className="legal-section">
                <div className="section-header">
                  <FaUserShield className="section-icon" />
                  <h2>Data Protection Measures</h2>
                </div>
                
                <div className="protection-measures">
                  <div className="measure">
                    <h4>🔒 Encryption</h4>
                    <p>All data in transit is encrypted using industry-standard TLS encryption</p>
                  </div>
                  
                  <div className="measure">
                    <h4>🏗️ Infrastructure Security</h4>
                    <p>Our systems are hosted on secure, SOC 2 compliant infrastructure</p>
                  </div>
                  
                  <div className="measure">
                    <h4>🔑 Access Controls</h4>
                    <p>Strict access controls limit who can access operational systems</p>
                  </div>
                  
                  <div className="measure">
                    <h4>📊 Monitoring</h4>
                    <p>Continuous monitoring for security threats and unauthorized access</p>
                  </div>
                </div>
              </section>

              <section id="third-party" className="legal-section">
                <h2>Third-Party Services</h2>
                
                <h3>🔗 OAuth Providers</h3>
                <p>
                  We use OAuth authentication with social media platforms (Twitter, GitHub, etc.) to verify 
                  account ownership. These interactions are governed by the respective platform's privacy policies:
                </p>
                <ul>
                  <li><a href="https://twitter.com/privacy" target="_blank" rel="noopener noreferrer">Twitter Privacy Policy</a></li>
                  <li><a href="https://docs.github.com/en/site-policy/privacy-policies/github-privacy-statement" target="_blank" rel="noopener noreferrer">GitHub Privacy Statement</a></li>
                </ul>

                <h3>📊 Analytics</h3>
                <p>
                  We use privacy-focused analytics tools to understand how our service is used. 
                  These tools are configured to respect user privacy and comply with data protection regulations.
                </p>
              </section>

              <section id="your-rights" className="legal-section">
                <h2>Your Privacy Rights</h2>
                
                <div className="rights-grid">
                  <div className="right">
                    <h4>🔍 Access</h4>
                    <p>Request information about what personal data we have</p>
                  </div>
                  
                  <div className="right">
                    <h4>✏️ Correction</h4>
                    <p>Request correction of inaccurate personal data</p>
                  </div>
                  
                  <div className="right">
                    <h4>🗑️ Deletion</h4>
                    <p>Request deletion of your personal data (where legally permissible)</p>
                  </div>
                  
                  <div className="right">
                    <h4>📋 Portability</h4>
                    <p>Request a copy of your data in a machine-readable format</p>
                  </div>
                </div>

                <div className="blockchain-limitation">
                  <h4>⚠️ Blockchain Data Limitations</h4>
                  <p>
                    Due to the immutable nature of blockchain technology, on-chain verification data 
                    cannot be deleted or modified. However, since this data doesn't contain personal 
                    information, it doesn't compromise your privacy.
                  </p>
                </div>
              </section>

              <section id="data-retention" className="legal-section">
                <h2>Data Retention</h2>
                
                <ul>
                  <li><strong>Verification Proofs:</strong> Stored permanently on blockchain</li>
                  <li><strong>Technical Logs:</strong> Retained for 90 days for security purposes</li>
                  <li><strong>Support Data:</strong> Retained for 2 years or as required by law</li>
                  <li><strong>Marketing Data:</strong> Retained until you opt out</li>
                </ul>
              </section>

              <section id="contact" className="legal-section">
                <h2>Contact Information</h2>
                
                <div className="contact-info">
                  <p>
                    If you have questions about this Privacy Policy or wish to exercise your privacy rights:
                  </p>
                  
                  <div className="contact-methods">
                    <div className="contact-method">
                      <strong>📧 Email:</strong>
                      <span>hello@passly.xyz</span>
                    </div>

                    <div className="contact-method">
                      <strong>🌐 Website:</strong>
                      <span>https://passly.xyz</span>
                    </div>
          
                  </div>

                  <p className="response-time">
                    We aim to respond to all privacy-related inquiries within 30 days.
                  </p>
                </div>
              </section>

              <section className="legal-section">
                <h2>Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy periodically. We will notify users of material 
                  changes by posting the updated policy on our website and updating the "Last Updated" 
                  date. Continued use of our service after changes constitutes acceptance of the updated policy.
                </p>
              </section>

            </div>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button 
        className={`return-to-top ${showScrollTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Return to top"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 19V5M5 12l7-7 7 7"/>
        </svg>
      </button>

      <style jsx>{`
        .legal-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
          color: #ffffff;
        }

        .legal-header {
          background: linear-gradient(135deg, rgba(15, 15, 35, 0.95) 0%, rgba(26, 26, 46, 0.95) 50%, rgba(22, 33, 62, 0.95) 100%);
          border-bottom: 1px solid rgba(124, 77, 255, 0.2);
          padding: 2rem 0 4rem 0;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          font-size: 0.9rem;
          margin-bottom: 2rem;
          transition: color 0.3s ease;
        }

        .back-link:hover {
          color: var(--color-primary);
        }

        .legal-title-section {
          text-align: center;
          max-width: 600px;
          margin: 0 auto;
        }

        .legal-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, rgba(124, 77, 255, 0.2) 0%, rgba(0, 204, 184, 0.2) 100%);
          border-radius: 50%;
          margin-bottom: 1.5rem;
          font-size: 2rem;
          color: var(--color-primary);
        }

        .legal-title {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 1rem;
          background: linear-gradient(90deg, #ffffff, rgba(255, 255, 255, 0.8));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .legal-subtitle {
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 1.5rem;
        }

        .legal-meta {
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.9rem;
        }

        .legal-content {
          padding: 4rem 0;
        }

        .legal-grid {
          display: grid;
          grid-template-columns: 250px 1fr;
          gap: 3rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .legal-toc {
          position: sticky;
          top: 2rem;
          height: fit-content;
          background: rgba(30, 30, 30, 0.6);
          border-radius: 12px;
          padding: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .legal-toc h3 {
          font-size: 1.1rem;
          margin-bottom: 1rem;
          color: var(--color-primary);
        }

        .legal-toc ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .legal-toc li {
          margin-bottom: 0.5rem;
        }

        .legal-toc a {
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          font-size: 0.9rem;
          transition: color 0.3s ease;
        }

        .legal-toc a:hover {
          color: var(--color-primary);
        }

        .legal-main {
          background: rgba(30, 30, 30, 0.4);
          border-radius: 16px;
          padding: 3rem;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .legal-section {
          margin-bottom: 3rem;
          padding-bottom: 2rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .legal-section:last-child {
          border-bottom: none;
          margin-bottom: 0;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .section-icon {
          color: var(--color-primary);
          font-size: 1.5rem;
        }

        .legal-section h2 {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: #ffffff;
        }

        .legal-section h3 {
          font-size: 1.3rem;
          font-weight: 600;
          margin: 2rem 0 1rem 0;
          color: rgba(255, 255, 255, 0.9);
        }

        .legal-section h4 {
          font-size: 1.1rem;
          font-weight: 600;
          margin: 1.5rem 0 0.75rem 0;
          color: rgba(255, 255, 255, 0.85);
        }

        .legal-section p {
          line-height: 1.7;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 1.25rem;
        }

        .legal-section ul {
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.7;
          margin-bottom: 1.25rem;
        }

        .legal-section li {
          margin-bottom: 0.5rem;
        }

        .privacy-highlight {
          background: linear-gradient(135deg, rgba(124, 77, 255, 0.1) 0%, rgba(0, 204, 184, 0.1) 100%);
          border: 1px solid rgba(124, 77, 255, 0.3);
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin: 1.5rem 0;
        }

        .info-card {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          padding: 1.5rem;
        }

        .info-card h4 {
          margin-top: 0;
        }

        .use-cases {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin: 1.5rem 0;
        }

        .use-case {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          padding: 1.25rem;
        }

        .use-case h4 {
          margin-top: 0;
          margin-bottom: 0.75rem;
        }

        .use-case p {
          margin-bottom: 0;
          font-size: 0.95rem;
        }

        .privacy-notice {
          background: rgba(0, 255, 136, 0.1);
          border: 1px solid rgba(0, 255, 136, 0.3);
          border-radius: 8px;
          padding: 1rem;
          margin: 1.5rem 0;
          color: #00ff88;
          text-align: center;
        }

        .blockchain-info {
          background: rgba(255, 255, 255, 0.02);
          border-radius: 12px;
          padding: 1.5rem;
          margin: 1.5rem 0;
        }

        .protection-measures {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin: 1.5rem 0;
        }

        .measure {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          padding: 1.25rem;
        }

        .measure h4 {
          margin-top: 0;
          margin-bottom: 0.75rem;
        }

        .measure p {
          margin-bottom: 0;
          font-size: 0.9rem;
        }

        .rights-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin: 1.5rem 0;
        }

        .right {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          padding: 1.25rem;
        }

        .right h4 {
          margin-top: 0;
          margin-bottom: 0.75rem;
        }

        .right p {
          margin-bottom: 0;
          font-size: 0.9rem;
        }

        .blockchain-limitation {
          background: rgba(255, 193, 7, 0.1);
          border: 1px solid rgba(255, 193, 7, 0.3);
          border-radius: 8px;
          padding: 1.25rem;
          margin: 1.5rem 0;
        }

        .blockchain-limitation h4 {
          color: #ffc107;
          margin-top: 0;
        }

        .contact-info {
          background: rgba(255, 255, 255, 0.02);
          border-radius: 12px;
          padding: 1.5rem;
        }

        .contact-methods {
          margin: 1.5rem 0;
        }

        .contact-method {
          display: flex;
          gap: 1rem;
          margin-bottom: 0.75rem;
          align-items: center;
        }

        .contact-method strong {
          min-width: 80px;
          color: var(--color-primary);
        }

        .response-time {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.6);
          margin-top: 1rem;
        }

        /* Return to Top Button Styles */
        .return-to-top {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          width: 3.5rem;
          height: 3.5rem;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(124, 77, 255, 0.9) 0%, rgba(0, 204, 184, 0.9) 100%);
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 
            0 4px 15px rgba(0, 0, 0, 0.3),
            0 0 10px rgba(124, 77, 255, 0.4);
          transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          opacity: 0;
          transform: translateY(20px) scale(0.9);
          pointer-events: none;
          z-index: 100;
        }
        
        .return-to-top.visible {
          opacity: 1;
          transform: translateY(0) scale(1);
          pointer-events: all;
        }
        
        .return-to-top:hover {
          transform: translateY(-5px);
          box-shadow: 
            0 6px 20px rgba(0, 0, 0, 0.4),
            0 0 15px rgba(124, 77, 255, 0.5);
        }
        
        .return-to-top:active {
          transform: translateY(0) scale(0.95);
        }
        
        .return-to-top::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%);
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        
        .return-to-top:hover::before {
          opacity: 1;
        }
        
        .return-to-top svg {
          width: 1.5rem;
          height: 1.5rem;
          stroke-width: 2.5;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .legal-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .legal-toc {
            position: static;
            order: -1;
          }
        }

        @media (max-width: 768px) {
          .return-to-top {
            bottom: 1.5rem;
            right: 1.5rem;
            width: 3rem;
            height: 3rem;
          }
          
          .return-to-top svg {
            width: 1.25rem;
            height: 1.25rem;
          }

          .legal-header {
            padding: 1.5rem 0 3rem 0;
          }

          .legal-title {
            font-size: 2rem;
          }

          .legal-subtitle {
            font-size: 1.1rem;
          }

          .legal-main {
            padding: 2rem 1.5rem;
          }

          .info-grid,
          .use-cases,
          .protection-measures,
          .rights-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .return-to-top {
            bottom: 1rem;
            right: 1rem;
            width: 2.75rem;
            height: 2.75rem;
          }

          .legal-content {
            padding: 2rem 0;
          }

          .legal-main {
            padding: 1.5rem 1rem;
          }

          .legal-section {
            margin-bottom: 2rem;
          }

          .legal-section h2 {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;